import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button,
  ButtonLink,
  Column,
  FormBox,
  InputCheckbox,
  InputRadio,
  Notice,
  PadlockIcon,
  PageActions,
  Row,
  Select,
  Text,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { escapeHtml, usePageHeader } from "@components/ow";
import { FormPage, FormPageSkeleton, FullWidth, focusFirstError } from "../../Common/FormPage";
import {
  dependentsOf,
  itemsFromSelection,
  resourceWords,
  selectionFromItems,
  selectionFromTemplate,
  withRequirements,
} from "./scope";

const SCOPE_OPTIONS = [
  { label: "Todos os projetos", value: "all" },
  { label: "Projetos específicos", value: "ids" },
];

// Grupo do catálogo mais delicado: vem por último (ordem do catálogo) e com destaque
const HIGHLIGHTED_GROUP = "confidential_data";

const CONFIDENTIAL_NOTICE =
  'Libera os dados das pessoas dos grupos confidenciais escolhidos. Só vale junto com as leituras acima ("Ver contratos", "Ver cronogramas", "Ver NFs", "Ver ponto" e "Ver horas").';

/** Erro do escopo vazio, nas palavras do tipo de recurso do item. */
function emptyScopeError(item) {
  const words = resourceWords(item);
  return words.all
    ? `Escolha pelo menos um ${words.one} ou marque "${words.all}".`
    : `Escolha pelo menos um ${words.one}.`;
}

// O `&&` vence o `> div { gap: 25px }` que a FormBox da lib põe nos filhos diretos.
const TemplateRow = styled.div`
  && {
    width: 100%;
    display: flex;
    align-items: flex-end;
    gap: 20px;
  }
`;

const TemplateSelect = styled.div`
  width: 420px;
`;

/** Pilha com a frase de apoio logo embaixo do checkbox, sem a margem do fieldset da lib. */
const Stack = styled.div`
  && {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${({ $gap }) => $gap}px;
  }

  fieldset {
    margin-bottom: 0;
  }
`;

// Escopo do item de horas, recuado para ficar sob o rótulo do checkbox
const Scope = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 12px 0 0 28px;

  fieldset {
    margin-bottom: 0;
  }
`;

// O erro da lib fica embaixo do campo, fora do fluxo: o espaço dele fica livre
const ResourcesField = styled.div`
  margin-bottom: ${({ $hasError }) => ($hasError ? 20 : 0)}px;
`;

const quoted = (items) => items.map((item) => `"${item.label}"`).join(", ");

// O checkbox e o radio da lib não são controlados (defaultChecked): a troca de valor
// remonta o campo pela `key`, e o foco volta para ele depois do clique.
const refocus = (id) => requestAnimationFrame(() => document.getElementById(id)?.focus());

/**
 * Permissões de um colaborador (só ADM). Mostra os itens do catálogo por grupo;
 * ao salvar, envia o conjunto final e o back grava a diferença. Os templates do
 * sistema só preenchem a tela: nada fica vinculado a eles.
 */
export default function UserPermissions() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Volta para a lista com os filtros de onde a pessoa veio
  const listPath = `/c/permissions${location.state?.listSearch || ""}`;
  const userPermissions = useService("userPermissions");
  const projectService = useService("projects");
  const groupsService = useService("confidentialGroups");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState({ groups: [], items: [], templates: [] });
  const [projects, setProjects] = useState([]);
  const [confidentialGroups, setConfidentialGroups] = useState([]);
  const [person, setPerson] = useState(null);
  const [selection, setSelection] = useState({});
  const [saved, setSaved] = useState({});
  const [templateKey, setTemplateKey] = useState("");
  // Template aplicado desde o último salvar/desfazer (para o aviso de substituição)
  const [appliedTemplate, setAppliedTemplate] = useState(null);
  // Os erros aparecem nos campos depois da primeira tentativa e se atualizam a cada mudança
  const [submitted, setSubmitted] = useState(false);

  const name = person?.name || "Colaborador";
  // O Layout escapa o título; o breadcrumb é texto
  usePageHeader({
    title: name,
    breadcrumb: [
      { page: "Permissões", route: listPath },
      { page: name, route: `/c/permissions/${userId}` },
    ],
  });

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  async function load() {
    setLoading(true);
    try {
      const [catalogResp, userResp, projectsResp, groupsResp] = await Promise.all([
        userPermissions.catalog(),
        userPermissions.getUser(userId),
        projectService.list("limit=9999&order=name:ASC"),
        groupsService.listGroups(),
      ]);
      const data = catalogResp?.data || {};
      setCatalog({ groups: data.groups || [], items: data.items || [], templates: data.templates || [] });
      applyResponse(userResp?.data);
      const list = projectsResp?.data?.list || [];
      setProjects(list.map((p) => ({ value: p.id, label: p.name })));
      setConfidentialGroups((groupsResp?.data || []).map((g) => ({ value: g.id, label: g.name })));
      setLoading(false);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.msg || "Erro ao carregar permissões");
      navigate(listPath);
    }
  }

  function applyResponse(data) {
    setPerson(data?.user || null);
    const current = selectionFromItems(data?.items || []);
    setSelection(current);
    setSaved(current);
    setAppliedTemplate(null);
    setSubmitted(false);
  }

  const dirty = useMemo(
    () =>
      JSON.stringify(itemsFromSelection(selection, catalog.items)) !==
      JSON.stringify(itemsFromSelection(saved, catalog.items)),
    [selection, saved, catalog]
  );

  // Recursos específicos sem nenhum recurso, por id do campo, na ordem do catálogo
  const fieldErrors = useMemo(() => {
    const errors = {};
    catalog.items.forEach((item) => {
      const scope = selection[item.key];
      if (item.scopable && scope && !scope.all && scope.ids.length === 0) {
        errors[`permission-resources-${item.key}`] = emptyScopeError(item);
      }
    });
    return errors;
  }, [selection, catalog]);

  const templateOptions = useMemo(
    () => catalog.templates.map((t) => ({ value: t.key, label: t.label })),
    [catalog]
  );
  const chosen = catalog.templates.find((t) => t.key === templateKey);

  function update(next) {
    setSelection(withRequirements(next, catalog.items));
  }

  // O checkbox e o radio da lib avisam duas vezes (no input e no fieldset): marcar, e não inverter.
  function toggleItem(item, checked) {
    const next = { ...selection };
    if (checked) next[item.key] = { all: !item.scopable, ids: [] };
    else delete next[item.key];
    update(next);
    refocus(`permission-${item.key}`);
  }

  function setScope(item, scope) {
    update({ ...selection, [item.key]: scope });
  }

  function applyTemplate() {
    if (!chosen) return;
    setSelection(selectionFromTemplate(chosen, catalog.items, selection));
    setAppliedTemplate(chosen);
  }

  function undoChanges() {
    setSelection(saved);
    setAppliedTemplate(null);
    setSubmitted(false);
  }

  async function handleSave() {
    setSubmitted(true);
    const invalid = Object.keys(fieldErrors);
    if (invalid.length) {
      focusFirstError(invalid);
      return;
    }
    setSaving(true);
    try {
      const resp = await userPermissions.setUser(userId, itemsFromSelection(selection, catalog.items));
      applyResponse(resp?.data);
      toast.success("Permissões salvas");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.msg || "Erro ao salvar permissões");
    } finally {
      setSaving(false);
    }
  }

  function renderResources(item, { label, placeholder, options, disabled }) {
    const scope = selection[item.key];
    const id = `permission-resources-${item.key}`;
    const error = submitted ? fieldErrors[id] : undefined;
    return (
      <ResourcesField $hasError={!!error}>
        <Row $gap={20}>
          <Column $columns={8}>
            <Select
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              $idForm={id}
              $label={label}
              $options={options}
              $errors={error}
              isDisabled={disabled}
              placeholder={placeholder}
              $textNoOptions={`Nenhum ${resourceWords(item).one} encontrado`}
              value={options.filter((o) => scope.ids.includes(o.value))}
              onChange={(chosenOptions) =>
                setScope(item, { all: false, ids: (chosenOptions || []).map((o) => o.value) })
              }
            />
          </Column>
        </Row>
      </ResourcesField>
    );
  }

  // Item que vale só para recursos específicos (grupos confidenciais), sem "todos"
  function renderGroupScope(item) {
    const hasGroups = confidentialGroups.length > 0;
    return (
      <Scope>
        {renderResources(item, {
          label: "Grupos",
          placeholder: hasGroups ? "Selecione os grupos" : "Nenhum grupo confidencial criado",
          options: confidentialGroups,
          disabled: !hasGroups,
        })}
      </Scope>
    );
  }

  function renderScope(item) {
    if (item.allows_all === false) return renderGroupScope(item);
    const scope = selection[item.key];
    const dependents = dependentsOf(item.key, selection, catalog.items);
    // Um item que depende deste e vale para todos obriga este a valer para todos
    const mustBeAll = dependents.some((d) => selection[d.key].all);
    const radioName = `permission-scope-${item.key}`;
    return (
      <Scope>
        <InputRadio
          key={`${radioName}-${scope.all}`}
          name={radioName}
          $options={SCOPE_OPTIONS}
          $isDisabled={mustBeAll}
          value={scope.all ? "all" : "ids"}
          onChange={(e) => {
            setScope(item, { all: e.target.value === "all", ids: [] });
            refocus(`${radioName}_${e.target.value}`);
          }}
        />
        {!scope.all
          ? renderResources(item, { label: "Projetos", placeholder: "Selecione os projetos", options: projects })
          : null}
        {dependents.length > 0 ? (
          <Text
            $text={`Inclui pelo menos os projetos de ${quoted(dependents)}.`}
            $variant="caption_medium"
            $color="N400"
          />
        ) : null}
      </Scope>
    );
  }

  function renderItem(item) {
    const checked = !!selection[item.key];
    const dependents = dependentsOf(item.key, selection, catalog.items);
    const locked = checked && dependents.length > 0;
    const id = `permission-${item.key}`;
    return (
      <div key={item.key}>
        <Stack $gap={4}>
          <InputCheckbox
            key={`${id}-${checked}`}
            $options={[{ name: id, label: item.label, value: checked }]}
            $isDisabled={locked}
            onChange={(e) => e.target.name === id && toggleItem(item, e.target.checked)}
          />
          <Text $text={item.description} $variant="caption_medium" $color="N400" />
          {locked ? (
            <Text $text={`Incluído por ${quoted(dependents)}.`} $variant="caption_medium" $color="N400" />
          ) : null}
        </Stack>
        {checked && item.scopable ? renderScope(item) : null}
      </div>
    );
  }

  function renderTemplates() {
    if (catalog.templates.length === 0) return null;
    const labels = new Map(catalog.items.map((item) => [item.key, item.label]));
    const hasHours =
      chosen && catalog.items.some((item) => item.scopable && item.allows_all !== false && chosen.items.includes(item.key));
    const kept = catalog.items.filter((item) => item.templatable === false).map((item) => `"${item.label}"`);
    return (
      <FormBox title="Aplicar template">
        <TemplateRow>
          <TemplateSelect>
            <Select
              $idForm="permission-template"
              $label="Template"
              $options={templateOptions}
              placeholder="Escolha um template"
              value={templateOptions.find((o) => o.value === templateKey) || null}
              onChange={(option) => setTemplateKey(option ? option.value : "")}
            />
          </TemplateSelect>
          {/* Com $noWidth o botão herda a largura do pai: o div o deixa do tamanho do texto */}
          <div>
            <Button
              $text="Aplicar template"
              $variant="outline"
              $isDisabled={!chosen || saving}
              $action={applyTemplate}
              $noWidth
            />
          </div>
        </TemplateRow>
        <FullWidth>
          <Stack $gap={4}>
            {chosen ? (
              <>
                <Text $text={chosen.description} $variant="caption_medium" $color="N400" />
                <Text
                  $text={`Itens: ${chosen.items.map((key) => labels.get(key) || key).join(", ")}.${
                    hasHours
                      ? " Os itens de horas entram em todos os projetos; você pode escolher projetos específicos antes de salvar."
                      : ""
                  }`}
                  $variant="caption_medium"
                  $color="N400"
                />
              </>
            ) : null}
            <Text
              $text={`Aplicar substitui o que está marcado abaixo${
                kept.length > 0 ? `, exceto ${kept.join(", ")}, que só sai desmarcando` : ""
              }. Nada é gravado até você clicar em Salvar permissões.`}
              $variant="caption_medium"
              $color="N400"
            />
          </Stack>
        </FullWidth>
        {appliedTemplate && dirty ? (
          <FullWidth>
            <Notice
              $variant="orange"
              $text={`Template <strong>"${escapeHtml(appliedTemplate.label)}"</strong> aplicado. Ao salvar, as permissões atuais de ${escapeHtml(
                person?.name || "este colaborador"
              )} serão substituídas por estas. Para voltar ao que está salvo, use "Desfazer alterações".`}
            />
          </FullWidth>
        ) : null}
      </FormBox>
    );
  }

  if (loading) return <FormPageSkeleton sections={[200, 160, 160, 300]} />;

  return (
    <FormPage
      notice={person?.email ? <Text $text={escapeHtml(person.email)} $variant="caption_medium" $color="N400" /> : null}
      actions={
        <PageActions>
          {dirty ? <ButtonLink $text="Desfazer alterações" $action={undoChanges} /> : null}
          <Button
            $text="Salvar permissões"
            $variant="primary"
            $action={handleSave}
            $isLoading={saving}
            $isDisabled={!dirty}
          />
        </PageActions>
      }
    >
      {renderTemplates()}

      {catalog.groups.map((group) => {
        const groupItems = catalog.items.filter((item) => item.group === group.key);
        if (groupItems.length === 0) return null;
        return (
          <FormBox key={group.key} title={group.label}>
            {group.key === HIGHLIGHTED_GROUP ? (
              <FullWidth>
                <Notice
                  $variant="orange"
                  $icon={<PadlockIcon />}
                  $text={CONFIDENTIAL_NOTICE}
                  $actions={
                    <Button
                      $text="Ver grupos confidenciais"
                      $variant="outline"
                      $size="small"
                      $action={() => navigate("/c/confidential-groups")}
                      $noWidth
                    />
                  }
                />
              </FullWidth>
            ) : null}
            <Stack $gap={20}>{groupItems.map(renderItem)}</Stack>
          </FormBox>
        );
      })}
    </FormPage>
  );
}
